package components.finishingUp

import com.meistercharts.charts.pv.stringsPlanning.PvStringsPlanningLayer
import it.neckar.commons.kotlin.js.getNotNull
import it.neckar.commons.kotlin.js.safeGet
import it.neckar.lizergy.model.configuration.moduleLayout.ResolvedModuleLayouts
import it.neckar.lizergy.model.configuration.quote.builder.ResolvedInverterConfiguration
import it.neckar.lizergy.model.stumps.AssemblyBasement
import it.neckar.lizergy.model.stumps.AssemblyRoof
import it.neckar.lizergy.model.stumps.entryFor
import it.neckar.lizergy.model.stumps.withEntry
import it.neckar.open.collections.fastForEach
import it.neckar.react.common.*
import it.neckar.react.common.form.*
import kotlinx.css.*
import kotlinx.html.classes
import react.*
import react.dom.*
import styled.*

val EditInverterMessdaten: FC<EditInverterMessdatenProps> = fc("EditInverterMessdaten") { props ->
  val assemblyRoofInverterMessdatenState = props::assemblyRoofInverterMessdatenState.getNotNull()
  val assemblyBasementInverterMessdatenState = props::assemblyBasementInverterMessdatenState.getNotNull()
  val inverterConfigurationIndex = props::inverterConfigurationIndex.safeGet()
  val inverterConfiguration = props::inverterConfiguration.safeGet()
  val moduleLayouts = props::moduleLayouts.safeGet()
  val previousInverterStringCount = props::previousInverterStringCount.safeGet()
  val editableStatus = props::editableStatus.safeGet()

  val assemblyRoofInverterMessdaten = assemblyRoofInverterMessdatenState.value.entryFor(inverterConfiguration.id, inverterConfigurationIndex) ?: AssemblyRoof.InverterMessdaten()
  val assemblyBasementInverterMessdaten = assemblyBasementInverterMessdatenState.value.entryFor(inverterConfiguration.id, inverterConfigurationIndex) ?: AssemblyBasement.InverterMessdaten()

  val inverterSerialNumber = useState(assemblyRoofInverterMessdaten.inverterSerialNumber)
  val stringMessdaten = useState(assemblyRoofInverterMessdaten.stringMessdaten)

  val assemblyRoofWetterbedingungen = useState(assemblyRoofInverterMessdaten.wetterbedingungen)
  val assemblyRoofUhrzeit = useState(assemblyRoofInverterMessdaten.uhrzeit)
  val assemblyRoofMonteur = useState(assemblyRoofInverterMessdaten.monteur)
  val assemblyRoofBemerkungen = useState(assemblyRoofInverterMessdaten.bemerkungen)

  val assemblyBasementWetterbedingungen = useState(assemblyBasementInverterMessdaten.wetterbedingungen)
  val assemblyBasementUhrzeit = useState(assemblyBasementInverterMessdaten.uhrzeit)
  val assemblyBasementMonteur = useState(assemblyBasementInverterMessdaten.monteur)
  val assemblyBasementBemerkungen = useState(assemblyBasementInverterMessdaten.bemerkungen)


  val assemblyRoofInverterMessdatenToSave = useMemo(
    inverterSerialNumber.value,
    stringMessdaten.value,
    assemblyRoofWetterbedingungen.value,
    assemblyRoofUhrzeit.value,
    assemblyRoofMonteur.value,
    assemblyRoofBemerkungen.value,
  ) {
    assemblyRoofInverterMessdaten.copy(
      inverterConfigurationId = inverterConfiguration.id,
      inverterSerialNumber = inverterSerialNumber.value,
      stringMessdaten = stringMessdaten.value,
      wetterbedingungen = assemblyRoofWetterbedingungen.value,
      uhrzeit = assemblyRoofUhrzeit.value,
      monteur = assemblyRoofMonteur.value,
      bemerkungen = assemblyRoofBemerkungen.value,
    )
  }

  val assemblyBasementInverterMessdatenToSave = useMemo(
    assemblyBasementWetterbedingungen.value,
    assemblyBasementUhrzeit.value,
    assemblyBasementMonteur.value,
    assemblyBasementBemerkungen.value,
  ) {
    assemblyBasementInverterMessdaten.copy(
      inverterConfigurationId = inverterConfiguration.id,
      wetterbedingungen = assemblyBasementWetterbedingungen.value,
      uhrzeit = assemblyBasementUhrzeit.value,
      monteur = assemblyBasementMonteur.value,
      bemerkungen = assemblyBasementBemerkungen.value,
    )
  }


  useEffect(assemblyRoofInverterMessdatenToSave) {
    assemblyRoofInverterMessdatenState.setter { old -> old.withEntry(assemblyRoofInverterMessdatenToSave, inverterConfigurationIndex) }
  }

  useEffect(assemblyBasementInverterMessdatenToSave) {
    assemblyBasementInverterMessdatenState.setter { old -> old.withEntry(assemblyBasementInverterMessdatenToSave, inverterConfigurationIndex) }
  }


  h5("mt-4 mb-3") { +"Messdaten ${inverterConfiguration.format()}" }

  div("my-3") {
    nullableFloatingInputField(
      valueAndSetter = inverterSerialNumber,
      fieldName = "inverterSerialNumber_${inverterConfiguration.id}_${inverterConfiguration.inverterId}",
      title = "Seriennummer",
      editableStatus = editableStatus,
    )
  }

  inverterConfiguration.relevantMpptConfigurations.fastForEach { mpptInputConfiguration ->
    b {
      +buildString {
        append("MPPT ")
        append('A' + mpptInputConfiguration.inputIndex) //this is a char-based addition (A..B..C..)
      }
    }

    mpptInputConfiguration.relevantStringConfigurations.fastForEach { stringConfiguration ->
      val mpptInputIndex = inverterConfiguration.relevantMpptConfigurations.indexOfFirst { it.id == mpptInputConfiguration.id }
      val stringIndex = mpptInputConfiguration.relevantStringConfigurations.indexOfFirst { it.id == stringConfiguration.id }

      val previousMpptInputStringCount = inverterConfiguration.previousMpptInputStringCount(mpptInputIndex)
      val globalStringIndex = previousInverterStringCount + previousMpptInputStringCount + stringIndex
      val stringColor = PvStringsPlanningLayer.Configuration.defaultStringColors.valueAt(globalStringIndex)
      val stringLabelBoxTextColor = PvStringsPlanningLayer.Configuration.defaultStringLabelBoxTextColors.valueAt(globalStringIndex)

      div("mb-3") {
        b("mb-2") {
          styledSpan {
            attrs {
              classes += "px-2"
              classes += "me-2"
            }
            css {
              backgroundColor = Color(stringColor.web)
              color = Color(stringLabelBoxTextColor.web)
            }
            +"${inverterConfigurationIndex + 1}.${mpptInputIndex + 1}.${stringIndex + 1}."
          }
          +stringConfiguration.format(moduleLayouts)
        }

        EditStringMessdaten {
          attrs {
            this.stringMessdaten = assemblyRoofInverterMessdaten[stringConfiguration.id] ?: AssemblyRoof.StringMessdaten.getEmpty(stringConfiguration.id)
            this.stringMessdatenSetter = stringMessdaten.setter
            this.editableStatus = editableStatus
          }
        }
      }
    }
  }

  div("row") {
    div("col-lg-6") {
      EditMessdaten {
        attrs {
          this.wetterbedingungen = assemblyRoofWetterbedingungen
          this.uhrzeit = assemblyRoofUhrzeit
          this.monteur = assemblyRoofMonteur
          this.bemerkungen = assemblyRoofBemerkungen
          this.suffix = "Dachmontage"
          this.editableStatus = editableStatus
        }
      }
    }

    div("col-lg-6") {
      EditMessdaten {
        attrs {
          this.wetterbedingungen = assemblyBasementWetterbedingungen
          this.uhrzeit = assemblyBasementUhrzeit
          this.monteur = assemblyBasementMonteur
          this.bemerkungen = assemblyBasementBemerkungen
          this.suffix = "Installation"
          this.editableStatus = editableStatus
        }
      }
    }
  }

}

external interface EditInverterMessdatenProps : Props {
  var assemblyRoofInverterMessdatenState: StateInstance<List<AssemblyRoof.InverterMessdaten>>
  var assemblyBasementInverterMessdatenState: StateInstance<List<AssemblyBasement.InverterMessdaten>>
  var inverterConfigurationIndex: Int
  var inverterConfiguration: ResolvedInverterConfiguration
  var moduleLayouts: ResolvedModuleLayouts
  var previousInverterStringCount: Int
  var editableStatus: EditableStatus
}
